import {
	MoreVert as MoreVertIcon,
	AutorenewOutlined,
	HistoryOutlined,
	HowToRegOutlined,
	CancelOutlined,
	MarkEmailReadOutlined,
	FindInPageOutlined,
	PriceCheckOutlined,
	ReceiptLongOutlined,
	PictureAsPdfOutlined,
} from "@mui/icons-material";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import React from "react";
import { useProfile } from "../../../context/ProfileContext";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { useToast } from "../../../shared/hooks";
import { IAgreement, IAgreementInstallment, SinisterAgreementService } from "../../../shared/services/api/sinister/SinisterAgreementService";
import { ModalAgencyTransferDetails } from "./Modals/ModalAgencyTransferDetails";
import { ModalCancelInstallment } from "./Modals/ModalCancelInstallment";
import { ModalInstallmentHistory } from "./Modals/ModalInstallmentHistory";
import { ModalManualSettleInstallment } from "./Modals/ModalManualSettleInstallment";
import { ModalReissueInstallment } from "./Modals/ModalReissueInstallment";
import { ModalRegisterAgencyTransfer } from "./Modals/ModalRegisterAgencyTransfer";
import { ModalResendBillet } from "./Modals/ModalResendBillet";
import { ModalSegundaVia } from "./Modals/ModalSegundaVia";

export interface IExtraInstallmentMenuItem {
	label: string;
	icon: React.ReactNode;
	onClick: () => void;
}

interface Props {
	agreement: IAgreement;
	installment: IAgreementInstallment;
	onChanged: () => void;
	// Opcional: usado pela tela de Controle de Cobranças pra injetar itens extras no topo do mesmo
	// menu (ex.: "Ver detalhes da parcela", "Ver Parcelas do Acordo"), em vez de duplicar toda a
	// listagem de ações num componente à parte.
	extraMenuItems?: IExtraInstallmentMenuItem[];
}

export function InstallmentRowMenu({ agreement, installment, onChanged, extraMenuItems }: Props) {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [openReissueModal, setOpenReissueModal] = React.useState(false);
	const [openHistoryModal, setOpenHistoryModal] = React.useState(false);
	const [openManualSettleModal, setOpenManualSettleModal] = React.useState(false);
	const [openCancelModal, setOpenCancelModal] = React.useState(false);
	const [openResendBilletModal, setOpenResendBilletModal] = React.useState(false);
	const [openSegundaViaModal, setOpenSegundaViaModal] = React.useState(false);
	const [openRegisterTransferModal, setOpenRegisterTransferModal] = React.useState(false);
	const [openTransferDetailsModal, setOpenTransferDetailsModal] = React.useState(false);
	const open = Boolean(anchorEl);
	const { isSuperAdmin } = useProfile();
	const isLegal = agreement.sector === "juridico";
	const isInstallmentClosed = installment.status === "paid" || installment.status === "canceled";
	const canReissue =
		(isSuperAdmin ||
			checkByProfileGuard(
				PermissionRolesEnum.AgreementsOptions,
				isLegal ? PermissionActionsEnum.ReissueInstallmentLegal : PermissionActionsEnum.ReissueInstallmentCollection
			)) &&
		!isInstallmentClosed &&
		agreement.status !== "broken";
	const canManualSettle =
		(isSuperAdmin ||
			checkByProfileGuard(
				PermissionRolesEnum.AgreementsOptions,
				isLegal ? PermissionActionsEnum.ManualSettleInstallmentLegal : PermissionActionsEnum.ManualSettleInstallmentCollection
			)) &&
		!isInstallmentClosed;
	const canCancel =
		(isSuperAdmin ||
			checkByProfileGuard(
				PermissionRolesEnum.AgreementsOptions,
				isLegal ? PermissionActionsEnum.CancelInstallmentLegal : PermissionActionsEnum.CancelInstallmentCollection
			)) &&
		!isInstallmentClosed;
	const isTransferRegistered = !!installment.agencyTransferRegistration;
	// Registrar repasse é uma ação independente da negociação — feita à parte, por parcela, só depois
	// do acordo fechado (ver PR-09).
	const canRegisterTransfer =
		(isSuperAdmin ||
			checkByProfileGuard(
				PermissionRolesEnum.AgreementsOptions,
				isLegal ? PermissionActionsEnum.RegisterAgencyTransferLegal : PermissionActionsEnum.RegisterAgencyTransferCollection
			)) &&
		!isTransferRegistered;
	// Mesma permissão usada pras outras ações de boleto (2ª via/nova via) — visualizar o PDF depende do
	// backend expor `installment.id` (ver comentário em IAgreementInstallment/getBoletoPdf).
	const canViewBoletoPdf =
		isSuperAdmin ||
		checkByProfileGuard(
			PermissionRolesEnum.AgreementsOptions,
			isLegal ? PermissionActionsEnum.ReissueInstallmentLegal : PermissionActionsEnum.ReissueInstallmentCollection
		);
	const toastHandler = useToast();

	const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
	const handleClose = () => setAnchorEl(null);
	const handleViewPdf = async () => {
		if (!installment.id) return;
		const result = await SinisterAgreementService.getBoletoPdf(installment.id);
		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
		}
	};

	return (
		<>
			<IconButton size="small" onClick={handleClick} aria-label="Ações da parcela">
				<MoreVertIcon fontSize="small" />
			</IconButton>

			<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
				{extraMenuItems?.map((item) => (
					<MenuItem
						key={item.label}
						onClick={() => {
							handleClose();
							item.onClick();
						}}
					>
						<ListItemIcon>{item.icon}</ListItemIcon>
						<ListItemText>{item.label}</ListItemText>
					</MenuItem>
				))}

				<Tooltip title={canManualSettle ? "" : "Sem permissão ou parcela indisponível"} placement="right">
					<span>
						<MenuItem
							disabled={!canManualSettle}
							onClick={() => {
								handleClose();
								setOpenManualSettleModal(true);
							}}
						>
							<ListItemIcon>
								<HowToRegOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Baixa Manual</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canCancel ? "" : "Sem permissão ou parcela indisponível"} placement="right">
					<span>
						<MenuItem
							disabled={!canCancel}
							onClick={() => {
								handleClose();
								setOpenCancelModal(true);
							}}
						>
							<ListItemIcon>
								<CancelOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Cancelar Parcela</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canReissue ? "" : "Sem permissão"} placement="right">
					<span>
						<MenuItem
							disabled={!canReissue}
							onClick={() => {
								handleClose();
								setOpenSegundaViaModal(true);
							}}
						>
							<ListItemIcon>
								<FindInPageOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Consultar 2ª Via</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip
					title={!canViewBoletoPdf ? "Sem permissão" : !installment.id ? "PDF indisponível para essa parcela" : ""}
					placement="right"
				>
					<span>
						<MenuItem
							disabled={!canViewBoletoPdf || !installment.id}
							onClick={() => {
								handleClose();
								handleViewPdf();
							}}
						>
							<ListItemIcon>
								<PictureAsPdfOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Visualizar Boleto (PDF)</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canReissue ? "" : "Sem permissão ou parcela indisponível"} placement="right">
					<span>
						<MenuItem
							disabled={!canReissue}
							onClick={() => {
								handleClose();
								setOpenReissueModal(true);
							}}
						>
							<ListItemIcon>
								<AutorenewOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Gerar Nova Via</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canReissue ? "" : "Sem permissão ou parcela indisponível"} placement="right">
					<span>
						<MenuItem
							disabled={!canReissue}
							onClick={() => {
								handleClose();
								setOpenResendBilletModal(true);
							}}
						>
							<ListItemIcon>
								<MarkEmailReadOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Reenviar Boleto</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<MenuItem
					onClick={() => {
						handleClose();
						setOpenHistoryModal(true);
					}}
				>
					<ListItemIcon>
						<HistoryOutlined fontSize="small" />
					</ListItemIcon>
					<ListItemText>Ver Histórico</ListItemText>
				</MenuItem>

				{!isTransferRegistered && (
					<Tooltip title={canRegisterTransfer ? "" : "Sem permissão"} placement="right">
						<span>
							<MenuItem
								disabled={!canRegisterTransfer}
								onClick={() => {
									handleClose();
									setOpenRegisterTransferModal(true);
								}}
							>
								<ListItemIcon>
									<PriceCheckOutlined fontSize="small" />
								</ListItemIcon>
								<ListItemText>Registrar Repasse</ListItemText>
							</MenuItem>
						</span>
					</Tooltip>
				)}

				{isTransferRegistered && (
					<MenuItem
						onClick={() => {
							handleClose();
							setOpenTransferDetailsModal(true);
						}}
					>
						<ListItemIcon>
							<ReceiptLongOutlined fontSize="small" />
						</ListItemIcon>
						<ListItemText>Detalhes do Repasse</ListItemText>
					</MenuItem>
				)}
			</Menu>

			<ModalReissueInstallment
				open={openReissueModal}
				agreementId={agreement.id}
				installment={installment}
				onClose={() => setOpenReissueModal(false)}
				onConfirm={() => {
					setOpenReissueModal(false);
					onChanged();
				}}
			/>
			<ModalManualSettleInstallment
				open={openManualSettleModal}
				agreementId={agreement.id}
				installment={installment}
				onClose={() => setOpenManualSettleModal(false)}
				onConfirm={() => {
					setOpenManualSettleModal(false);
					onChanged();
				}}
			/>
			<ModalCancelInstallment
				open={openCancelModal}
				agreementId={agreement.id}
				installment={installment}
				onClose={() => setOpenCancelModal(false)}
				onConfirm={() => {
					setOpenCancelModal(false);
					onChanged();
				}}
			/>
			<ModalInstallmentHistory
				open={openHistoryModal}
				installmentNumber={installment.number}
				history={agreement.installmentHistory}
				onClose={() => setOpenHistoryModal(false)}
			/>
			<ModalResendBillet
				open={openResendBilletModal}
				agreementId={agreement.id}
				installment={installment}
				onClose={() => setOpenResendBilletModal(false)}
				onConfirm={() => {
					setOpenResendBilletModal(false);
					onChanged();
				}}
			/>
			<ModalSegundaVia
				open={openSegundaViaModal}
				agreementId={agreement.id}
				installment={installment}
				onClose={() => setOpenSegundaViaModal(false)}
			/>
			<ModalRegisterAgencyTransfer
				open={openRegisterTransferModal}
				agreementId={agreement.id}
				installment={installment}
				onClose={() => setOpenRegisterTransferModal(false)}
				onConfirm={() => {
					setOpenRegisterTransferModal(false);
					onChanged();
				}}
			/>
			<ModalAgencyTransferDetails open={openTransferDetailsModal} installment={installment} onClose={() => setOpenTransferDetailsModal(false)} />
		</>
	);
}
